// ============================================================
// TMR Portal Admin — como uma taxa se lê na tela, para os dois escopos que existem
// ============================================================

/**
 * The rate types the legacy charges in reais rather than as a percentage.
 *
 * `5` is the Taxa de Saque and `6` the Operação de Crédito; every other type in `tipo_taxas` is a
 * percentage. The ids are hard-coded on purpose: the catalogue (`GET /companies/rate-catalog`) publishes
 * each type's id and label and says nothing about the unit, and `tipo_valor_id` — the column that looks
 * like it would — is vestigial (`15` §5: it has two values, and none of the 20.196 rows uses the second).
 * Reading the unit off the type id is the only rule there is, so it is written down once instead of being
 * repeated as a `%` suffix on every screen.
 */
const CURRENCY_RATE_TYPE_IDS: ReadonlySet<number> = new Set([5, 6]);

/** What a rate's value is denominated in. */
export type RateUnit = 'percent' | 'currency';

/**
 * The unit a rate of this type is charged in.
 *
 * @param rateTypeId The legacy `tipo_taxas` id.
 */
export function rateUnit(rateTypeId: number): RateUnit {
  return CURRENCY_RATE_TYPE_IDS.has(rateTypeId) ? 'currency' : 'percent';
}

/**
 * The suffix the value of this type carries inside a form field — where the number is being typed, so the
 * unit sits beside the input instead of around the value.
 *
 * @param rateTypeId The legacy `tipo_taxas` id.
 */
export function rateUnitSuffix(rateTypeId: number): string {
  return rateUnit(rateTypeId) === 'currency' ? 'R$' : '%';
}

/**
 * A rate value as it is read on screen, in the unit its type is charged in: `3,03%` or `R$ 5,00`.
 *
 * Until 2026-08-14 every rate in the portal was written with a `%`, the two amount-in-reais types included.
 * On the Empresa's tab that was a wrong label; on `/taxas`, which lists all the types side by side, it
 * would be a wrong label over money with the right one one line above it.
 *
 * @param value The numeric value.
 * @param rateTypeId The legacy `tipo_taxas` id it belongs to.
 */
export function formatRateValue(value: number, rateTypeId: number): string {
  const decimal = value.toFixed(2).replace('.', ',');
  return rateUnit(rateTypeId) === 'currency' ? `R$ ${decimal}` : `${decimal}%`;
}

/**
 * How a rate's installment range reads.
 *
 * Structural rather than typed to one contract: an Empresa's rate and a line of the global template carry
 * the same band and have to read the same way — the operator compares one against the other.
 *
 * @param band The installment range, inclusive at both ends.
 */
export function bandLabel(band: { minInstallment: number; maxInstallment: number }): string {
  if (band.minInstallment === band.maxInstallment) {
    return band.minInstallment === 1 ? '1 parcela' : `${band.minInstallment} parcelas`;
  }
  return `${band.minInstallment} a ${band.maxInstallment} parcelas`;
}

/**
 * What a reconciliation with the legacy did, as one sentence.
 *
 * The result used to be discarded, so an operator who clicked "Sincronizar" and saw the list unchanged had
 * no way to tell "nothing to bring" from "it did not run" — the two look identical on screen.
 *
 * @param result The counts the sync returned, for an Empresa's rates or for the global template.
 */
export function syncSummary(result: { created: number; updated: number; removed: number }): string {
  const parts: string[] = [];
  if (result.created > 0) parts.push(result.created === 1 ? '1 taxa trazida' : `${result.created} taxas trazidas`);
  if (result.updated > 0) parts.push(result.updated === 1 ? '1 atualizada' : `${result.updated} atualizadas`);
  if (result.removed > 0) parts.push(result.removed === 1 ? '1 removida' : `${result.removed} removidas`);

  return parts.length === 0
    ? 'Sincronizado com o sistema legado: nada mudou.'
    : `Sincronizado com o sistema legado: ${parts.join(', ')}.`;
}
